/**
🔗 TESTER CETTE PAGE : http://dev.sellerie.local/devis
📝 Ctrl+Click sur le lien ci-dessus pour ouvrir dans le navigateur

SELLERIE MARINE - PAGE GESTION DEVIS

📋 DESCRIPTION:
Page React principale pour la gestion et visualisation des devis.
Interface complète avec filtrage, recherche, actions groupées et génération commandes.

🎯 RESPONSABILITÉS:
- Affichage liste complète des devis avec informations détaillées
- Filtrage par statut et recherche textuelle multi-critères
- Calcul dynamique des statuts selon spécification backend
- Actions individuelles (voir/modifier, supprimer) et groupées
- Génération commandes fournisseur groupées avec aperçu
- Interface de sélection multiple avec barre d'actions flottante
- Formatage automatique des montants et dates

🔗 DÉPENDANCES:
- DevisApiService: CRUD devis et récupération données
- ClientService: Informations clients pour affichage noms
- CommandeFournisseurService: Génération commandes groupées
- CommandeHistoriqueService: Traçabilité des commandes
- PreviewModal: Composant aperçu commandes avant validation
- React Router: Navigation vers configurateur et nouveau devis

📡 INTERACTIONS:
- Services: Chargement devis, clients, génération commandes
- Navigation: Liens vers configurateur et création nouveau devis
- Interface: Filtres, recherche, sélection multiple, actions
- Modal: Aperçu commandes avec confirmation téléchargement
- Historique: Traçabilité automatique des commandes générées

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Statuts calculés dynamiquement selon logique backend
- Interface responsive avec tableaux adaptatifs
- Sélection multiple avec actions groupées (commandes fournisseur)
- Formatage localisé des montants et dates (fr-FR)
- Barre d'actions flottante pour sélection multiple
- Aperçu commandes avec détails avant téléchargement
- Traçabilité automatique dans historique commandes
*/
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { devisApiService } from '../services/DevisApiService';
import { clientService } from '../services/ClientService';
import { Devis as DevisType } from '../services/types';
import { commandeFournisseurService, CommandeGroupee } from '../services/CommandeFournisseurService';
import { commandeHistoriqueService } from '../services/CommandeHistoriqueService';
import { exportCommandeService } from '../services/ExportCommandeService';
import PreviewModal from '../components/CommandeFournisseur/PreviewModal';
import { apiFetch } from '../services/httpClient';
import AuthContext from '../contexts/AuthContext';

// Types pour l'affichage des devis
interface DevisDisplay {
  id: string;
  reference: string;
  client: string;
  date: string;
  projet: string;
  montant: number;
  montantPF: number;
  montantDeplacement: number;
  statut: 'NOUVEAU' | 'BROUILLON' | 'ENREGISTRE' | 'COMPLET' | 'EN_ATTENTE_RETOUR_CLIENT' | 'ACCEPTE' | 'TERMINE_LIVRE' | 'ANNULE' | 'ABANDONNE';
  articles: number;
  hasCommandeFournisseurEffective: boolean;
  commandeFournisseurARevalider: boolean;
  lastCommandeFournisseurEffectiveAt?: string;
}

const Devis: React.FC = () => {
  const { access } = useContext(AuthContext);
  const canEdit = access?.access_mode === 'active' || access?.access_mode === 'trial';
  const canViewPdf = canEdit || access?.access_mode === 'read_only';
  const [devis, setDevis] = useState<DevisDisplay[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'actifs' | 'archives'>('actifs');
  const [filtreStatut, setFiltreStatut] = useState<string>('tous');
  const [recherche, setRecherche] = useState<string>('');
  const [clients, setClients] = useState<Record<string, any>>({});
  const [devisSelectionnes, setDevisSelectionnes] = useState<string[]>([]);
  const [generationEnCours, setGenerationEnCours] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [commandePreview, setCommandePreview] = useState<CommandeGroupee | null>(null);
  const [soumisTVA, setSoumisTVA] = useState<boolean>(true);

  // Fonction pour calculer le statut dynamiquement selon la même logique que le backend
  const calculerStatutDynamique = (devis: any) => {
    // Vérifications par ordre de priorité selon la spécification backend
    if (devis?.marque_comme_livre) {
      return 'TERMINE_LIVRE';
    }
    
    if (devis?.marque_comme_accepte) {
      return 'ACCEPTE';
    }
    
    if (devis?.pdf_genere_le) {
      return 'EN_ATTENTE_RETOUR_CLIENT';
    }
    
    // Utiliser le statut du devis existant pour les autres cas
    return devis?.statut || 'NOUVEAU';
  };

  // Fonction pour supprimer un devis
  const handleSupprimerDevis = async (devisId: string, reference: string) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le devis ${reference} ? Cette action est irréversible.`)) {
      try {
        await devisApiService.deleteDevis(devisId);
        // Recharger la liste des devis
        setDevis(prevDevis => prevDevis.filter(d => d.id !== devisId));

      } catch (error) {
        console.error('Erreur lors de la suppression du devis:', error);
        alert('Erreur lors de la suppression du devis. Veuillez réessayer.');
      }
    }
  };

  const handleArchiverDevis = async (devisId: string, reference: string) => {
    const confirme = window.confirm(`Archiver le devis ${reference} ? Il sera déplacé dans Archivés.`);
    if (!confirme) return;
    try {
      await devisApiService.archiveDevis(devisId);
      setDevis(prev => prev.filter(d => d.id !== devisId));
      setDevisSelectionnes(prev => prev.filter(id => id !== devisId));
    } catch (error) {
      console.error("Erreur lors de l'archivage du devis:", error);
      alert("Erreur lors de l'archivage du devis. Veuillez réessayer.");
    }
  };

  const handleRestaurerDevis = async (devisId: string, reference: string) => {
    const confirme = window.confirm(`Restaurer le devis ${reference} ? Il reviendra dans Actifs.`);
    if (!confirme) return;
    try {
      await devisApiService.unarchiveDevis(devisId);
      setDevis(prev => prev.filter(d => d.id !== devisId));
      setDevisSelectionnes(prev => prev.filter(id => id !== devisId));
    } catch (error) {
      console.error('Erreur lors de la restauration du devis:', error);
      alert('Erreur lors de la restauration du devis. Veuillez réessayer.');
    }
  };

  // Charger les devis depuis le service
  useEffect(() => {
    const fetchDevis = async () => {
      try {
        setLoading(true);
        setDevisSelectionnes([]);
        
        // Récupérer les devis depuis le service
        const devisData = await devisApiService.getDevis(activeTab === 'archives');

        // R?cup?rer le statut TVA de l'entreprise
        try {
          const entrepriseData = await apiFetch<any>('/entreprise', {
          });
          setSoumisTVA(entrepriseData.soumisTVA ?? entrepriseData.soumisATVA ?? true);
        } catch (error) {
          console.error('Erreur lors de la r?cup?ration du statut TVA:', error);
        }

        // Récupérer les clients pour afficher leurs noms
        const clientsData = await clientService.getClients();
        const clientsMap: Record<string, any> = {};
        clientsData.forEach(client => {
          clientsMap[client.id || ''] = client;
        });
        setClients(clientsMap);
        
        // Transformer les données des devis pour l'affichage
        const devisDisplay: DevisDisplay[] = devisData.map(devis => {
          const client = clientsMap[(devis as any).clientId || (devis as any).client || ''];
          const clientName = client 
            ? (client.societe || `${client.prenom} ${client.nom}`)
            : 'Client inconnu';
          
          // Calculer le statut dynamiquement
          const statutDynamique = calculerStatutDynamique(devis);
          
          // Récupérer les montants PF et déplacement depuis finalisationData
          const finalisationData = (devis as any).finalisationData || {};
          const montantPF = finalisationData.petitesFournitures?.montantCalcule || 0;
          const montantDeplacement = finalisationData.fraisDeplacementConfig?.montantCalcule || 0;
          const hasCommande = Boolean((devis as any).has_commande_fournisseur_effective);
          const commandeARevalider = Boolean((devis as any).commande_fournisseur_a_revalider);
          const lastCommandeAt = (devis as any).last_commande_fournisseur_effective_at || undefined;
          
          return {
            id: devis.id || '',
            reference: devis.reference || '',
            client: clientName,
            date: devis.date ? new Date(devis.date).toLocaleDateString('fr-FR') : '',
            projet: devis.projet || '',
            montant: (devis as any).montantHT || 0,  // ✅ CORRECTION: Afficher montantHT au lieu de montantTTC
            montantPF: montantPF,
            montantDeplacement: montantDeplacement,
            statut: statutDynamique as any, // Utiliser le statut calculé dynamiquement
            articles: devis.articles ? devis.articles.length : 0,
            hasCommandeFournisseurEffective: hasCommande,
            commandeFournisseurARevalider: commandeARevalider,
            lastCommandeFournisseurEffectiveAt: lastCommandeAt,
          };
        });
        
        setDevis(devisDisplay);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des devis:', error);
        setLoading(false);
      }
    };
    
    fetchDevis();
  }, [activeTab]);

  // Filtrer les devis en fonction du statut et de la recherche
  const devisFiltres = devis.filter((d) => {
    const matchStatut = filtreStatut === 'tous' || d.statut === filtreStatut;
    const matchRecherche =
      d.reference.toLowerCase().includes(recherche.toLowerCase()) ||
      d.client.toLowerCase().includes(recherche.toLowerCase()) ||
      (d.projet && d.projet.toLowerCase().includes(recherche.toLowerCase()));
    return matchStatut && matchRecherche;
  });

  // Fonction pour afficher le statut avec la bonne couleur selon la spec
  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'NOUVEAU':
        return 'bg-gray-100 text-gray-600'; // Gris clair #9CA3AF
      case 'BROUILLON':
        return 'bg-gray-200 text-gray-700'; // Gris foncé #6B7280
      case 'ENREGISTRE':
        return 'bg-blue-100 text-blue-700'; // Bleu #3B82F6
      case 'COMPLET':
        return 'bg-green-100 text-green-700'; // Vert #10B981
      case 'EN_ATTENTE_RETOUR_CLIENT':
        return 'bg-orange-100 text-orange-700'; // Orange #F59E0B
      case 'ACCEPTE':
        return 'bg-blue-200 text-blue-800'; // Bleu foncé #2563EB
      case 'TERMINE_LIVRE':
        return 'bg-purple-100 text-purple-700'; // Violet #8B5CF6
      case 'ANNULE':
        return 'bg-red-100 text-red-700'; // Rouge #EF4444
      case 'ABANDONNE':
        return 'bg-yellow-100 text-yellow-700'; // Jaune #F59E0B
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour afficher le nom du statut avec emoji selon la spécification
  const getStatusLabel = (statut: string) => {
    switch (statut) {
      case 'NOUVEAU':
        return '📄 Nouveau';
      case 'BROUILLON':
        return '📝 Brouillon';
      case 'ENREGISTRE':
        return '💾 Enregistré';
      case 'COMPLET':
        return '✅ Complet';
      case 'EN_ATTENTE_RETOUR_CLIENT':
        return '⏳ En attente retour client';
      case 'ACCEPTE':
        return '✍️ Accepté par le client';
      case 'TERMINE_LIVRE':
        return '🎉 Terminé / Livré';
      case 'ANNULE':
        return '❌ Annulé';
      case 'ABANDONNE':
        return '🚫 Abandonné';
      default:
        return statut;
    }
  };

  // Fonction pour formater les montants en euros
  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant);
  };

  const handleApercuCommandeFournisseur = async () => {
    if (devisSelectionnes.length === 0) return;

    try {
      setGenerationEnCours(true);
      
      // 🔍 DEBUG 1 : IDs sélectionnés
      console.log('🔍 [SELECTION] IDs cochés:', devisSelectionnes);
      console.log('🔍 [SELECTION] Nombre:', devisSelectionnes.length);
      
      // Récupérer les devis complets sélectionnés
      const devisComplets = await Promise.all(
        devisSelectionnes.map(id => devisApiService.getDevisById(id))
      );
      
      // 🔍 DEBUG 2 : Devis récupérés
      console.log('🔍 [RECUPERES] Nombre:', devisComplets.length);
      console.log('🔍 [RECUPERES] Références:', devisComplets.map(d => d.reference));
      console.log('🔍 [RECUPERES] IDs:', devisComplets.map(d => d.id));
      
      // Générer la commande groupée
      const commande = await commandeFournisseurService.genererCommandeGroupee(devisComplets);
      
      // 🔍 DEBUG 3 : Commande générée
      console.log('🔍 [COMMANDE] Devis inclus:', commande.devisInclus);
      console.log('🔍 [COMMANDE] Nombre de matières:', commande.matieres.length);
      console.log('🔍 [COMMANDE] Détail matières:', commande.matieres.map(m => ({
        nom: m.nom,
        quantite: m.quantiteMetres,
        devisOrigines: m.devisOrigines.map(d => d.reference)
      })));
      
      // Stocker la commande et afficher l'aperçu
      setCommandePreview(commande);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Erreur lors de la génération de la commande:', error);
      alert('Erreur lors de la génération de la commande fournisseur. Veuillez réessayer.');
    } finally {
      setGenerationEnCours(false);
    }
  };

  const handleExportCommande = async (format: 'pdf' | 'excel' | 'csv' | 'txt') => {
    if (!commandePreview) return;

    try {
      setGenerationEnCours(true);

      if (format === 'pdf') {
        await exportCommandeService.exportToPDF(commandePreview);
      } else if (format === 'excel') {
        await exportCommandeService.exportToExcel(commandePreview);
      } else if (format === 'csv') {
        await exportCommandeService.exportToCSV(commandePreview);
      } else {
        const contenu = commandeFournisseurService.formaterPourExport(commandePreview);
        const blob = new Blob([contenu], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${commandePreview.numeroCommande}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      await commandeHistoriqueService.ajouterCommandeAHistorique(commandePreview, devisSelectionnes);

      setShowPreviewModal(false);
      setCommandePreview(null);
      setDevisSelectionnes([]);

      alert(`Commande ${commandePreview.numeroCommande} exportÃƒÂ©e en ${format.toUpperCase()} et ajoutÃƒÂ©e ÃƒÂ  l'historique.`);
    } catch (error) {
      console.error(`Erreur lors de l'export ${format}:`, error);
      alert(`Erreur lors de l'export ${format.toUpperCase()}. Veuillez rÃƒÂ©essayer.`);
    } finally {
      setGenerationEnCours(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des devis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Gestion des devis</h1>
      <div className="flex items-center space-x-3">
        <button
          onClick={handleApercuCommandeFournisseur}
          disabled={devisSelectionnes.length === 0 || generationEnCours}
          className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            title={devisSelectionnes.length === 0 ? 'Sélectionnez un ou plusieurs devis pour générer une commande fournisseur.' : undefined}
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {`Commande fournisseur${devisSelectionnes.length > 0 ? ` (${devisSelectionnes.length})` : ''}`}
          </button>
          <Link
            to="/devis/nouveau"
            className={`btn-primary flex items-center ${canEdit ? '' : 'opacity-60 cursor-not-allowed'}`}
            onClick={(e) => {
              if (!canEdit) {
                e.preventDefault();
                alert("Action disponible uniquement en essai ou abonnement actif.");
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Nouveau devis
          </Link>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          className={`px-4 py-2 rounded-md border ${activeTab === 'actifs' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-200'}`}
          onClick={() => setActiveTab('actifs')}
        >
          Actifs
        </button>
        <button
          className={`px-4 py-2 rounded-md border ${activeTab === 'archives' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-200'}`}
          onClick={() => setActiveTab('archives')}
        >
          Archivés
        </button>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
            <label htmlFor="statut" className="form-label">
              Filtrer par statut:
            </label>
            <select
              id="statut"
              className="input-field w-full md:w-auto"
              value={filtreStatut}
              onChange={(e) => setFiltreStatut(e.target.value)}
            >
              <option value="tous">Tous les devis</option>
              <option value="NOUVEAU">📄 Nouveau</option>
              <option value="BROUILLON">📝 Brouillon</option>
              <option value="ENREGISTRE">💾 Enregistré</option>
              <option value="COMPLET">✅ Complet</option>
              <option value="EN_ATTENTE_RETOUR_CLIENT">⏳ En attente retour client</option>
              <option value="ACCEPTE">✍️ Accepté par le client</option>
              <option value="TERMINE_LIVRE">🎉 Terminé / Livré</option>
              <option value="ANNULE">❌ Annulé</option>
              <option value="ABANDONNE">🚫 Abandonné</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="Rechercher..."
              className="input-field w-full md:w-64"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
            <button
              className="bg-gray-100 p-2 rounded-md hover:bg-gray-200"
              onClick={() => setRecherche('')}
              title="Effacer la recherche"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Liste des devis */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    checked={devisSelectionnes.length === devisFiltres.length && devisFiltres.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setDevisSelectionnes(devisFiltres.map(d => d.id));
                      } else {
                        setDevisSelectionnes([]);
                      }
                    }}
                  />
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Référence
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client / Projet
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Date
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Art.
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56">
                  {soumisTVA ? 'Montant HT' : 'Montant net'}
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Statut
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {devisFiltres.length > 0 ? (
                devisFiltres.map((devis) => (
                  <tr key={devis.id}>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={devisSelectionnes.includes(devis.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setDevisSelectionnes([...devisSelectionnes, devis.id]);
                          } else {
                            setDevisSelectionnes(devisSelectionnes.filter(id => id !== devis.id));
                          }
                        }}
                      />
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{devis.reference}</span>
                        {devis.hasCommandeFournisseurEffective && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200"
                            title={`Fournitures commandées : une commande fournisseur envoyée ou reçue est associée à ce devis.${devis.lastCommandeFournisseurEffectiveAt ? ` Dernière commande le ${new Date(devis.lastCommandeFournisseurEffectiveAt).toLocaleDateString('fr-FR')}.` : ''}`}
                          >
                            Fournitures commandées
                          </span>
                        )}
                        {devis.commandeFournisseurARevalider && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                            title="À revalider : le devis a été modifié après la dernière commande fournisseur (envoyée/reçue)."
                          >
                            À revalider
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-500">
                      <div className="max-w-xs">
                        <div className="font-medium text-gray-900 truncate">{devis.client}</div>
                        {devis.projet && (
                          <div className="text-xs text-gray-500 truncate">{devis.projet}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                      {devis.date}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 text-center">
                      {devis.articles}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-500">
                      <div>
                        <div className="font-medium text-gray-900">{formatMontant(devis.montant)}</div>
                        {/* Afficher ligne supplémentaire si PF ou déplacement et statut COMPLET+ */}
                        {['COMPLET', 'EN_ATTENTE_RETOUR_CLIENT', 'ACCEPTE', 'TERMINE_LIVRE'].includes(devis.statut) && 
                         (devis.montantPF > 0 || devis.montantDeplacement > 0) && (
                          <div className="text-xs text-gray-500 italic">
                            (dont PF et déplacement : {formatMontant(devis.montantPF + devis.montantDeplacement)})
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(devis.statut)}`}>
                        {getStatusLabel(devis.statut)}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-1">
                        <Link 
                          to={`/devis/configurateur/${devis.id}`} 
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-200"
                          title="Voir/Modifier"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </Link>
                        {activeTab === 'actifs' ? (
                          <button
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-yellow-700 hover:text-yellow-800 hover:bg-yellow-50 transition-colors duration-200"
                            onClick={() => handleArchiverDevis(devis.id, devis.reference)}
                            title="Archiver"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v8m4-4H8m12-5V7a2 2 0 00-2-2H6a2 2 0 00-2 2v1m0 0v9a2 2 0 002 2h12a2 2 0 002-2V8H4z" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 hover:text-green-800 hover:bg-green-50 transition-colors duration-200"
                            onClick={() => handleRestaurerDevis(devis.id, devis.reference)}
                            title="Restaurer"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v6h6M20 20v-6h-6M5 19a9 9 0 0110-14M19 5a9 9 0 01-10 14" />
                            </svg>
                          </button>
                        )}
                        <button 
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors duration-200"
                          onClick={() => handleSupprimerDevis(devis.id, devis.reference)}
                          title="Supprimer"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun devis ne correspond à vos critères de recherche.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Barre d'actions flottante pour la sélection multiple */}
      {devisSelectionnes.length > 0 && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white shadow-lg rounded-lg border border-gray-200 p-4 z-50">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">
                {devisSelectionnes.length} devis sélectionné{devisSelectionnes.length > 1 ? 's' : ''}
              </span>
              <button
                onClick={() => setDevisSelectionnes([])}
                className="text-gray-400 hover:text-gray-600"
                title="Désélectionner tout"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="h-6 border-l border-gray-300"></div>
            
            <button
              onClick={handleApercuCommandeFournisseur}
              disabled={generationEnCours}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {generationEnCours ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Préparation...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  📦 Aperçu commande fournisseur
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Modal de prévisualisation */}
      <PreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setCommandePreview(null);
        }}
        commande={commandePreview}
        onExport={handleExportCommande}
        isGenerating={generationEnCours}
      />
    </div>
  );
};

export default Devis;